import React, { useEffect, useState, useRef, useCallback } from 'react'
import { RemoveAssessmentEvidence, UploadAssessmentEvidence } from '../../../services/QuizService';
import { useParams } from 'react-router-dom';
import { AWS_File_URL, BASE_URL, UPLOAD_ROOT_PATH } from '../../../utils/Constants';
import { download } from '../../../utils/FileDownload';
import { handleUploadToS3BucketUrl } from '../../common/uploadFileToS3';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { removePosition, setPositon } from '../../../store/actions';

function HybridEvidences({question,attempt,updateQues,isAssessmentReview,isPreview = false,assignmentData}) {
  const [photo , setPhoto] = useState(false);
  const [video , setVideo] = useState(false);
  const [observation , setObservation] = useState(false);
  const [fileDetails,setFileDetails] = useState([])
  const dispatch = useDispatch();
  
  // Create loading states that are keyed by question ID and evidence type
  const [loadingStates, setLoadingStates] = useState({});
  
  const {aid} = useParams();
  
  // Keep track of active uploads to prevent race conditions
  const activeUploadsRef = useRef(new Set());
  const currentQuestionRef = useRef(null);

  // Update current question ref whenever question changes
  useEffect(() => {
    currentQuestionRef.current = question;
  }, [question]);

  useEffect(() => {
      setPhoto(question?.evidence_photo)
      setVideo(question?.evidence_video)
      setObservation(question?.evidence_lecturer_observation)
      setFileDetails(question?.studentEvidence || [])
  }, [question]);

  // Helper function to get/set loading state for specific question and evidence type
  const getLoadingState = (questionId, evidenceType) => {
    const key = `${questionId}_${evidenceType}`;
    return loadingStates[key] || false;
  };

  const setLoadingState = (questionId, evidenceType, loading) => {
    const key = `${questionId}_${evidenceType}`;
    setLoadingStates(prev => ({
      ...prev,
      [key]: loading
    }));
  };

  const uploadEvidence = useCallback(async(file, type) => {
    // Capture the current question at the time of upload initiation
    const uploadQuestion = currentQuestionRef.current;
    const uploadKey = `${uploadQuestion.qid}_${type}_${Date.now()}`;
    
    // Prevent duplicate uploads
    if (activeUploadsRef.current.has(uploadKey)) {
      return;
    }
    
    activeUploadsRef.current.add(uploadKey);
    if (!Swal.isVisible()) {
      Swal.fire({
        icon: "info",
        title: "Uploading evidence...",
      })
    }

    dispatch(setPositon(aid))

    if(isPreview){
      let arr = [...fileDetails];
      
      // Create a proper file object with the data you need
      const fileObj = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        id : Math.random()*1000-234+2256+Math.random(),
        // Add any other properties you need from the File object
        evidence_type: type,
        url :  URL.createObjectURL(file),
        qid: uploadQuestion.qid 
      };
      
      arr.push(fileObj);
      setFileDetails(arr);
      updateQues(uploadQuestion, arr);
      // Swal.close(); 
      activeUploadsRef.current.delete(uploadKey);
      return;
    }

    // Set loading state for the specific question and evidence type
    setLoadingState(uploadQuestion.qid, type, true);
    
    try {
      const formData = new FormData();
      formData.append('assessment_id',aid);
      formData.append('attempted_no',attempt);
      formData.append('qid',uploadQuestion.qid); // Use the captured question ID
      formData.append('evidence_type',type);
      
      const user = localStorage.getItem("user");
      const userData = JSON.parse(user);
      const randomString = Math.random().toString(36).slice(2, 7);
      const filePath =`Students/${userData?.id}_${userData?.student_crm_id}_${userData?.first_name}_${userData?.last_name}/${assignmentData?.id}_${assignmentData?.name}/${assignmentData?.name}_${userData?.student_crm_id}_${Date.now()}_evidence_${randomString}.${file?.name?.split(".").pop()}`;
     
      let awsFileUploadedUrl = [];

      awsFileUploadedUrl.push({
        file_name: filePath,
        file_type: file.type,
      });

      // uploading to aws directly
      const resultData = await handleUploadToS3BucketUrl(awsFileUploadedUrl, file);
      if(!resultData?.length){
        throw new Error("AWS upload failed");
      }

      resultData.forEach((result) => {
        formData.append(`uploadFilePath`, result.path);
        formData.append(`file_size`, result.size);
      })

      const res = await UploadAssessmentEvidence(formData);
      
      // Only update if we're still on the same question or if this is the correct question
      if (currentQuestionRef.current?.qid === uploadQuestion.qid) {
        setFileDetails(res.data?.getEvidences);
        updateQues(uploadQuestion, res.data?.getEvidences);
      } else {
        // If question has changed, still update the correct question
        updateQues(uploadQuestion, res.data?.getEvidences);
      }
      if (!Swal.isVisible()) {

        Swal.close()
        Swal.fire({
          icon: "success",
          title: "Your evidence has been uploaded successfully!",
          timer: 1000,
        });
      }
        dispatch(removePosition(aid))
      
    } catch (err) {
      dispatch(removePosition(aid))
      console.log("HybridEvidences -> uploadAssessmentEvidence: Error: " + err);
      Swal.close()
      Swal.fire({
        icon: "error",
        title: "Failed to Upload Evidence",
        text: "There was a problem uploading your file. Please try again.",
        timer: 2000,
      });
    } finally {
      setLoadingState(uploadQuestion.qid, type, false);
      activeUploadsRef.current.delete(uploadKey);
    }
  }, [aid, attempt, assignmentData, isPreview, updateQues]);

  const removeEvidence = useCallback((id, type) => {
    // Capture the current question at the time of removal initiation
    const removeQuestion = currentQuestionRef.current;
    
    if(isPreview){
      let arr = [...fileDetails];
      
      arr = arr.filter((d,i) => {
        return d?.id != id
      })

      setFileDetails(arr);
      updateQues(removeQuestion, arr);
      return;
    }
    
    // Set loading state for the specific question and evidence type
    setLoadingState(removeQuestion.qid, type, true);
    
    const data = {
      assessment_id:aid,
      attempted_no:attempt,
      qid:removeQuestion.qid, // Use the captured question ID
      evidence_type:type,
      id:id
    }
    
    RemoveAssessmentEvidence(data)
    .then((res) => {
      // Only update if we're still on the same question or update the correct question
      if (currentQuestionRef.current?.qid === removeQuestion.qid) {
        setFileDetails(res.data?.getEvidences);
        updateQues(removeQuestion, res.data?.getEvidences);
      } else {
        updateQues(removeQuestion, res.data?.getEvidences);
      }
    })
    .catch(err => {
      console.log("HybridEvidences -> removeAssessmentEvidence: Error: " + err);
    })
    .finally(() => {
      setLoadingState(removeQuestion.qid, type, false);
    });
  }, [aid, attempt, fileDetails, isPreview, updateQues]);

  // Clean up active uploads when component unmounts
  useEffect(() => {
    return () => {
      activeUploadsRef.current.clear();
    };
  }, []);

  return (
    (question?.question_type == "Document Upload" || question?.question_type == "Long Answer" || question?.question_type == "Evidence Only") ?

      <>
        {(photo || video || observation) ? <div className='font14semi my-4'>Evidence Required</div> : null}
        <div className='evidence-row'>
          {photo ? <div>{fileDetails?.filter(d => !d.evidence_type)?.length ? <i className='fas fa-check-circle text-success'></i> : <i className='fas fa-times-circle text-danger'></i>}<i className='fal fa-camera mx-1'></i>Photo</div> : null}
          {video ? <div>{fileDetails?.filter(d => d.evidence_type)?.length ? <i className='fas fa-check-circle text-success'></i> : <i className='fas fa-times-circle text-danger'></i>}<i className='fal fa-video mx-1'></i>Video</div> : null}
          {observation ? <div><i className='fas fa-times-circle lect-icon'></i><i className='fal fa-user-check mx-1'></i>Lecturer Observation</div> : null}
        </div>
        <div className="row">
          {photo ? <PhotoEvidence 
            removeEvidence={removeEvidence} 
            uploadEvidence={uploadEvidence} 
            evidenceData={fileDetails?.filter(d => !d.evidence_type)} 
            loading={getLoadingState(question.qid, 0)}
            isAssessmentReview={isAssessmentReview} 
            isPreview={isPreview} 
            question={question}
          /> : null}
          {video ? <VideoEvidence 
            removeEvidence={removeEvidence} 
            uploadEvidence={uploadEvidence} 
            evidenceData={fileDetails?.filter(d => d.evidence_type)} 
            loading={getLoadingState(question.qid, 1)}
            isAssessmentReview={isAssessmentReview} 
            isPreview={isPreview} 
            question={question}
          /> : null}
        </div>
      </> : null
  )
}

function PhotoEvidence({uploadEvidence, evidenceData, removeEvidence, loading, isAssessmentReview,isPreview}) {
  const handleFileInputClick = (e) => {
    // Reset the value when clicked
    e.target.value = '';
  };
    return (
        (!isAssessmentReview || evidenceData.length > 0) ? <div className='col-lg-6 d-flex flex-column'>
            <div className='f-bold heading-text-on-hold'><i className='fal fa-camera'></i> Photo Evidence</div> 
            <div className="form-group atttach-file mt-2 mb-3">
          {!isAssessmentReview ? <form>
            <label>
              <i className={`fal ${loading ? 'fa-cog fa-spin' : 'fa-paperclip'}`}></i>
              <span>Add Attachment</span>
              <input
                type="file"
                className="form-control form-control-aatch-file d-none"
                onClick={handleFileInputClick}
                disabled={loading}
                onChange={(e) => {uploadEvidence(e.target.files[0], 0) }}
                accept='.jpg, .png, .jpeg'
              />
            </label>
          </form> : null}
            </div>
            {evidenceData?.length > 0 && evidenceData.map((item, index) => {
                return (
                  <>
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center w-100"
                    >
                      {!isPreview ? (
                        <a
                          className="flex-1 maxw65"
                          href={
                            item?.evidence_url?.startsWith("https://")
                              ? item.evidence_url
                              : (item?.fileUploadedFrom
                                  ? AWS_File_URL
                                  : BASE_URL + "/") +
                                item?.evidence_url
                                  ?.replaceAll(`${UPLOAD_ROOT_PATH}/`, "")
                                  ?.replace("public/", "")
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item?.evidence_url?.split("/")?.pop()}
                        </a>
                      ) : (
                        <a
                          className="flex-1 maxw65"
                          href={item?.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item?.name}
                        </a>
                      )}

                      <div className="w-5 h-5 rounded-circle">
                        {!isAssessmentReview ? (
                          <button
                            className="btn btn-danger rounded-circle"
                            disabled={loading}
                            onClick={() => removeEvidence(item.id, 0)}
                          >
                            <i className="fal fa-trash-alt"></i>
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary rounded-circle"
                            disabled={loading}
                              onClick={() => {

                                let url = "";

                                if (item?.evidence_url?.startsWith("https://")) {
                                  url = item.evidence_url;
                                } else if (item?.fileUploadedFrom == '1') {
                                  url = AWS_File_URL + item?.evidence_url;
                                } else {
                                  url =
                                    BASE_URL +
                                    "/" +
                                    item?.evidence_url
                                      ?.replaceAll(`${UPLOAD_ROOT_PATH}/`, "")
                                      ?.replace("public/", "");
                                }


                                const fileName = item?.evidence_url?.split("/").pop();                                
                                const isFromAWS = item?.fileUploadedFrom == '1';
                                download(url, fileName, !isFromAWS);
                              }}
                          >
                            <i className="fal fa-download"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <hr className="w-100" />
                  </>
                );
              })
            }
        </div> : null
    )
}

function VideoEvidence({uploadEvidence, evidenceData, removeEvidence, loading, isAssessmentReview,isPreview}) {
  const handleFileInputClick = (e) => {
    // Reset the value when clicked
    e.target.value = '';
  };
    return (
      (!isAssessmentReview || evidenceData.length > 0) ? <div className='col-lg-6 d-flex flex-column'>
            <div className='f-bold heading-text-on-hold'><i className='fal fa-video'></i> Video Evidence</div>
            <div className="form-group atttach-file mt-2 mb-3">
                  {!isAssessmentReview ? <label>
                    <i className={`fal ${loading ? 'fa-cog fa-spin' : 'fa-paperclip'}`}></i>
                    <span>Add Attachment</span>
                    <input
                      type="file"
                      className="form-control form-control-aatch-file d-none"
                      onClick={handleFileInputClick}
                      disabled={loading}
                      onChange={(e) => uploadEvidence(e.target.files[0],1)}
                      accept='.mp4, .avi, .mov'
                    />                
                  </label> : null}
            </div>
            {
              evidenceData?.length > 0 && evidenceData.map((item, index) => {
                return (
                  <>
                    <div key={index} className='d-flex justify-content-between align-items-center w-100'>
                      {
                        !isPreview ? (
                          <a
                            className="flex-1 maxw65"
                            href={
                              item?.evidence_url?.startsWith("https://")
                                ? item.evidence_url
                                : item?.fileUploadedFrom == '1'
                                  ? AWS_File_URL + item.evidence_url
                                  : BASE_URL +
                                  "/" +
                                  (item.evidence_url
                                    ?.replaceAll(`${UPLOAD_ROOT_PATH}/`, "")
                                    ?.replace("public/", ""))
                            }
                            target="_blank"
                          >
                            {item?.evidence_url?.split("/")?.pop()}
                          </a>
                        ) : (
                          <a className="flex-1 maxw65" href={item?.url} target="_blank">
                            {item?.name}
                          </a>
                        )
                      }
                      <div className='w-5 h-5 rounded-circle'>
                        {
                          !isAssessmentReview ? (
                            <button
                              className="btn btn-danger rounded-circle"
                              disabled={loading}
                              onClick={() => removeEvidence(item.id, 1)}
                            >
                              <i className="fal fa-trash-alt"></i>
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary rounded-circle"
                              disabled={loading}
                                onClick={() => {
                                  let url = "";

                                  if (item?.evidence_url?.startsWith("https://")) {
                                    url = item.evidence_url;
                                  } else if (item?.fileUploadedFrom == '1') {
                                    url = AWS_File_URL + item.evidence_url;
                                  } else {
                                    const cleanedPath = item?.evidence_url
                                      ?.replaceAll(`${UPLOAD_ROOT_PATH}/`, "")
                                      ?.replace("public/", "");
                                    url = BASE_URL + "/" + cleanedPath;
                                  }

                                  const fileName = item?.evidence_url?.split("/").pop();
                                  const isFromAWS = item?.fileUploadedFrom == '1';

                                  download(url, fileName, !isFromAWS);
                                }}
                            >
                              <i className="fal fa-download"></i>
                            </button>
                          )
                        }
                      </div>
                    </div>
                    <hr className='w-100'/>
                  </>
                )
              })
            }
        </div> : null
    )
}

export default HybridEvidences